/**
 * Zoom-ul hartii de inventar: bara de jos (- / sina / + / Fit / 100%), rotita
 * mouse-ului si tastele + / - / 0.
 *
 * Limitele sunt ale panzei (`minZoom` / `maxZoom` din `drawMap`), iar sina
 * merge pe LOGARITM: de la 5% la 10% e acelasi drum ca de la 150% la 300%.
 * Pe o sina liniara, tot ce e sub 100% ar sta inghesuit in primul sfert.
 */

/** Cel mai mic zoom: o retea de cateva sute de aparate incape intreaga. */
export const MIN_ZOOM = 0.05
/** Cel mai mare: o iconita cat un deget, eticheta citibila de departe. */
export const MAX_ZOOM = 3
/** Un clic pe + sau -: un sfert mai mare / mai mic. */
export const ZOOM_STEP = 1.25
/** Pozitiile sinei (0..SLIDER_MAX); mii de trepte = miscare lina. */
export const SLIDER_MAX = 1000

/** Ce ii trebuie zoom-ului din panza (un subset din `Cy`). */
export type ZoomCy = {
  zoom: ((level: number | { level: number; renderedPosition: { x: number; y: number } }) => void) & (() => number)
  width: () => number
  height: () => number
}

export function clampZoom(level: number, min = MIN_ZOOM, max = MAX_ZOOM): number {
  if (!Number.isFinite(level)) return min
  return Math.min(max, Math.max(min, level))
}

/** Zoom -> pozitia pe sina (0..SLIDER_MAX), pe logaritm. */
export function sliderFromZoom(level: number, min = MIN_ZOOM, max = MAX_ZOOM): number {
  const z = clampZoom(level, min, max)
  return Math.round(((Math.log(z) - Math.log(min)) / (Math.log(max) - Math.log(min))) * SLIDER_MAX)
}

/** Pozitia pe sina -> zoom. Inversa lui `sliderFromZoom`. */
export function zoomFromSlider(value: number, min = MIN_ZOOM, max = MAX_ZOOM): number {
  const t = Math.min(SLIDER_MAX, Math.max(0, value)) / SLIDER_MAX
  return clampZoom(Math.exp(Math.log(min) + t * (Math.log(max) - Math.log(min))), min, max)
}

/** Procentul afisat in bara: 5% ... 300%. */
export function zoomPercent(level: number): string {
  return `${Math.round(level * 100)}%`
}

/**
 * Zoom in jurul MIJLOCULUI panzei vizibile - nodul din centru ramane pe loc.
 * (Rotita face zoom in jurul cursorului; bara n-are cursor pe panza.)
 */
export function zoomAtCenter(cy: ZoomCy, level: number): void {
  cy.zoom({ level: clampZoom(level), renderedPosition: { x: cy.width() / 2, y: cy.height() / 2 } })
}

/** Un pas: `+1` mareste cu ZOOM_STEP, `-1` micsoreaza. */
export function zoomStep(cy: ZoomCy, direction: 1 | -1): void {
  zoomAtCenter(cy, direction > 0 ? cy.zoom() * ZOOM_STEP : cy.zoom() / ZOOM_STEP)
}

/**
 * Cat se schimba zoom-ul la o miscare de rotita.
 *
 * Nu lasam rotita pe seama lui Cytoscape: build-ul din `plugins/cytoscape`
 * isi da seama ca e un mouse abia dupa patru miscari, imparte apoi pasul la
 * marimea lui, si cu `wheelSensitivity` 0.2 (cat era) o treapta de rotita
 * facea 0,5-1% - de la 50% la 100% trebuiau o suta de trepte. Masurat in Edge
 * cu cytoscape.min.js de aici: 10 trepte = +7% la 0.2, +42% la 1, +100% la 2;
 * si orice valoare care face mouse-ul lin face touchpad-ul sa sara, fiindca
 * acelasi numar inmulteste doua feluri de miscari.
 *
 * Aici: o treapta de mouse (deltaY ~100 px, sau 3 randuri in Firefox) = ~16%,
 * deci de la 50% la 100% in cinci trepte. Un touchpad trimite multe miscari
 * mici (deltaY 1-10) si ia proportional mai putin pe fiecare, deci ramane lin.
 * Ciupitul pe touchpad vine ca rotita cu `ctrlKey` si pasi mici - primeste un
 * coeficient mai mare, ca sa tina pasul cu degetele.
 * Plafon pe o singura miscare: un mouse "liber" (fara trepte) sau o pagina
 * intreaga (deltaMode 2) nu sar dintr-o data de la 10% la 300%.
 */
export function wheelFactor(ev: { deltaY: number; deltaMode?: number; ctrlKey?: boolean }): number {
  let dy = Number(ev.deltaY) || 0
  if (ev.deltaMode === 1) dy *= 33 // randuri -> pixeli, ca la Cytoscape
  else if (ev.deltaMode === 2) dy *= 400 // pagini
  const rate = ev.ctrlKey ? 0.01 : 0.0015
  const factor = Math.exp(-dy * rate)
  return Math.min(1.5, Math.max(1 / 1.5, factor))
}

/**
 * Prinde rotita pe invelisul panzei, in faza de capturare - inainte s-o vada
 * Cytoscape (care asculta pe containerul dinauntru) - si face zoom in jurul
 * cursorului. Numai pe panza: rotita peste bara de zoom sau peste notite nu
 * face nimic. Intoarce functia care o dezleaga.
 */
export function attachWheelZoom(wrap: HTMLElement, canvas: HTMLElement, cy: () => ZoomCy | null): () => void {
  const onWheel = (ev: WheelEvent) => {
    const target = ev.target as Node | null
    if (!target || !canvas.contains(target)) return
    const c = cy()
    if (!c) return
    ev.preventDefault()
    ev.stopPropagation()
    const rect = canvas.getBoundingClientRect()
    const at = { x: ev.clientX - rect.left, y: ev.clientY - rect.top }
    c.zoom({ level: clampZoom(c.zoom() * wheelFactor(ev)), renderedPosition: at })
  }
  wrap.addEventListener('wheel', onWheel, { capture: true, passive: false })
  return () => wrap.removeEventListener('wheel', onWheel, { capture: true })
}

/** Tasta -> ce face: `in`, `out`, `fit`, sau nimic. */
export function zoomKey(ev: { key: string; ctrlKey?: boolean; metaKey?: boolean; altKey?: boolean; target?: EventTarget | null }): 'in' | 'out' | 'fit' | null {
  if (ev.ctrlKey || ev.metaKey || ev.altKey) return null // Ctrl + / Ctrl - raman ale browserului
  const t = ev.target as HTMLElement | null
  if (t && t.tagName) {
    const tag = t.tagName.toLowerCase()
    const type = tag === 'input' ? (t as HTMLInputElement).type : ''
    /* Intr-un camp de scris, + si - sunt litere. Sina de zoom (range) nu e camp de scris. */
    if (tag === 'textarea' || tag === 'select' || t.isContentEditable || (tag === 'input' && type !== 'range')) return null
  }
  if (ev.key === '+' || ev.key === '=') return 'in'
  if (ev.key === '-' || ev.key === '_') return 'out'
  if (ev.key === '0') return 'fit'
  return null
}
